'use client';

import { useEffect, useRef, useState } from 'react';

import { useSearchParams } from 'next/navigation';

import { PasswordRefresh } from '@/components/auth/PasswordRefresh';
import { PasswordReset } from '@/components/auth/PasswordReset';
import { usePopup } from '@/hooks/use-popup';
import { WithLang } from '@/types/general';
import { getScrollRem } from '@/utils/window-utils';

import { AuthPopupKeys } from './auth.types';
import { Login } from './Login';
import { Signup } from './Signup';

interface Props {
    onClose: () => void;
    isOpened: boolean;
}

export const Auth = (props: WithLang<Props>) => {
    const { LOGIN, SIGNUP, REFRESH, RESET } = AuthPopupKeys;
    const refAuthContainer = useRef<HTMLDivElement>(null);
    const [topPos] = useState<string>(getScrollRem() >= 2 ? 'top-[3.75rem]' : 'top-[4.75rem]');

    const params = useSearchParams();
    const token = params.get('token') || '';

    const { isVisible, setIsVisible, openPopup } = usePopup({
        [LOGIN]: false,
        [SIGNUP]: false,
        [RESET]: false,
        [REFRESH]: false,
    });

    // closes the modal when an outside click occurs
    useEffect(() => {
        const handleOutsideClick = (event: MouseEvent) => {
            if (refAuthContainer.current && !refAuthContainer.current.contains(event.target as Node)) {
                props.onClose();
            }
        };
        document.addEventListener('click', handleOutsideClick);
        return () => document.removeEventListener('click', handleOutsideClick);
    }, []);

    const updatePopupState = (key: keyof typeof isVisible) => {
        const updatedState: typeof isVisible = {
            [LOGIN]: false,
            [SIGNUP]: false,
            [REFRESH]: false,
            [RESET]: false,
        };
        updatedState[key] = true;
        setIsVisible(updatedState);
    };

    useEffect(() => {
        if (props.isOpened && token) {
            openPopup(RESET);
            return;
        }
        if (props.isOpened && !token) {
            openPopup(LOGIN);
            return;
        }
    }, [props.isOpened]);

    return (
        <div
            ref={refAuthContainer}
            className={`absolute right-0 z-[1] flex w-full flex-col gap-y-8 overflow-y-auto rounded-b-md border border-solid border-gray-500 bg-white px-4 py-2 pb-12 pt-8 sm:px-14 md:max-w-xl md:px-8 ${topPos}`}
        >
            {isVisible.login && (
                <Login
                    lang={props.lang}
                    isVisible={isVisible[LOGIN]}
                    onRedirect={(key) => updatePopupState(key)}
                    onClose={props.onClose}
                />
            )}
            {isVisible.signup && (
                <Signup
                    lang={props.lang}
                    isVisible={isVisible[SIGNUP]}
                    onRedirect={(key) => updatePopupState(key)}
                    onClose={props.onClose}
                />
            )}
            {isVisible.refresh && (
                <PasswordRefresh
                    lang={props.lang}
                    isVisible={isVisible[REFRESH]}
                    onRedirect={(key) => updatePopupState(key)}
                    onClose={props.onClose}
                />
            )}
            {isVisible.reset && (
                <PasswordReset
                    token={token}
                    lang={props.lang}
                    isVisible={isVisible[RESET]}
                    onRedirect={(key) => updatePopupState(key)}
                    onClose={props.onClose}
                />
            )}
        </div>
    );
};
